import React, { useMemo, useState } from 'react';
import jsPDF from 'jspdf';
import {
  FaDownload,
  FaPrint,
  FaRedo,
  FaIdBadge,
  FaPlus,
  FaTrash,
} from 'react-icons/fa';
import SEO from '../components/SEO';
import LogoUploader from '../components/LogoUploader';
import { drawPdfLogo } from '../utils/logoUpload';

const PAYMENT_MODES = ['Bank Transfer', 'Cheque', 'Cash', 'UPI'];

const TEMPLATES = [
  { id: 'classic', label: 'Classic Corporate', accent: '#1d4ed8' },
  { id: 'modern', label: 'Modern Banner', accent: '#0f766e' },
  { id: 'minimal', label: 'Minimal Mono', accent: '#111827' },
  { id: 'sidebar', label: 'Side Strip', accent: '#7c3aed' },
  { id: 'compact', label: 'Compact Two-Column', accent: '#dc2626' },
];

const hexToRgbArr = (hex) => {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
};

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const now = new Date();
const currentMonth = MONTHS[now.getMonth()];
const currentYear = now.getFullYear();

const formatINR = (n) =>
  Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const numberToWordsIndian = (num) => {
  num = Math.floor(Number(num) || 0);
  if (num === 0) return 'Zero Rupees Only';
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
    'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const two = (n) => n < 20 ? ones[n] : tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
  const three = (n) => {
    const h = Math.floor(n / 100);
    const r = n % 100;
    return (h ? ones[h] + ' Hundred' + (r ? ' ' : '') : '') + (r ? two(r) : '');
  };
  let res = '';
  const crore = Math.floor(num / 10000000); num %= 10000000;
  const lakh = Math.floor(num / 100000); num %= 100000;
  const thousand = Math.floor(num / 1000); num %= 1000;
  if (crore) res += two(crore) + ' Crore ';
  if (lakh) res += two(lakh) + ' Lakh ';
  if (thousand) res += two(thousand) + ' Thousand ';
  if (num) res += three(num);
  return res.trim() + ' Rupees Only';
};

const newRow = (label = '', amount = 0) => ({
  id: Math.random().toString(36).slice(2, 9),
  label,
  amount,
});

const SalarySlipGenerator = () => {
  const [template, setTemplate] = useState('classic');
  const [logo, setLogo] = useState(null);

  const [company, setCompany] = useState({
    name: 'Acme Technologies Pvt. Ltd.',
    address: 'Plot 12, Cyber City, Gurugram 122002',
    phone: '+91 98765 43210',
    email: 'hr@acmetech.in',
  });

  const [employee, setEmployee] = useState({
    name: 'Rahul Sharma',
    empId: 'ACM-1042',
    designation: 'Senior Software Engineer',
    department: 'Engineering',
    doj: '15/03/2021',
    pan: 'ABCDE1234F',
    uan: '101234567890',
    bankAcc: 'XXXX XXXX 4521',
    bankName: 'HDFC Bank',
    paymentMode: 'Bank Transfer',
  });

  const [period, setPeriod] = useState({
    month: currentMonth,
    year: String(currentYear),
    workingDays: 30,
    paidDays: 30,
    lop: 0,
  });

  const [earnings, setEarnings] = useState([
    newRow('Basic Salary', 40000),
    newRow('House Rent Allowance', 16000),
    newRow('Conveyance Allowance', 1600),
    newRow('Medical Allowance', 1250),
    newRow('Special Allowance', 11150),
  ]);

  const [deductions, setDeductions] = useState([
    newRow('Provident Fund (PF)', 1800),
    newRow('Professional Tax', 200),
    newRow('Income Tax (TDS)', 3500),
  ]);

  const updateRow = (setter) => (id, patch) =>
    setter((list) => list.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const addRow = (setter, label = 'New Item') => () =>
    setter((list) => [...list, newRow(label, 0)]);
  const removeRow = (setter) => (id) =>
    setter((list) => list.filter((r) => r.id !== id));

  const totals = useMemo(() => {
    const grossEarnings = earnings.reduce((s, r) => s + Number(r.amount || 0), 0);
    const totalDeductions = deductions.reduce((s, r) => s + Number(r.amount || 0), 0);
    const netPay = grossEarnings - totalDeductions;
    return {
      grossEarnings,
      totalDeductions,
      netPay,
      netPayRounded: Math.round(netPay),
      netInWords: numberToWordsIndian(Math.round(netPay)),
    };
  }, [earnings, deductions]);

  // ---------- PDF helpers ----------
  const periodLabel = `${period.month} ${period.year}`;

  const drawEarningsDeductionsTable = (doc, startY, pageW, margin, accent) => {
    const [r, g, b] = hexToRgbArr(accent);
    const tableW = pageW - margin * 2;
    const colW = tableW / 2;
    const rowH = 7;
    const maxRows = Math.max(earnings.length, deductions.length);

    // Header
    doc.setFillColor(r, g, b);
    doc.rect(margin, startY, tableW, rowH, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Earnings', margin + 3, startY + 4.8);
    doc.text('Amount (Rs.)', margin + colW - 3, startY + 4.8, { align: 'right' });
    doc.text('Deductions', margin + colW + 3, startY + 4.8);
    doc.text('Amount (Rs.)', margin + tableW - 3, startY + 4.8, { align: 'right' });

    let y = startY + rowH;
    doc.setTextColor(40, 40, 40);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    for (let i = 0; i < maxRows; i++) {
      if (i % 2 === 1) {
        doc.setFillColor(248, 250, 252);
        doc.rect(margin, y, tableW, rowH, 'F');
      }
      const e = earnings[i];
      const d = deductions[i];
      if (e) {
        doc.text(String(e.label), margin + 3, y + 4.8);
        doc.text(formatINR(e.amount), margin + colW - 3, y + 4.8, { align: 'right' });
      }
      if (d) {
        doc.text(String(d.label), margin + colW + 3, y + 4.8);
        doc.text(formatINR(d.amount), margin + tableW - 3, y + 4.8, { align: 'right' });
      }
      y += rowH;
    }

    // Totals row
    doc.setFillColor(240, 244, 248);
    doc.rect(margin, y, tableW, rowH + 1, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(20, 20, 20);
    doc.text('Gross Earnings', margin + 3, y + 5.2);
    doc.text(formatINR(totals.grossEarnings), margin + colW - 3, y + 5.2, { align: 'right' });
    doc.text('Total Deductions', margin + colW + 3, y + 5.2);
    doc.text(formatINR(totals.totalDeductions), margin + tableW - 3, y + 5.2, { align: 'right' });

    // Outer border
    doc.setDrawColor(220, 220, 220);
    doc.rect(margin, startY, tableW, y + rowH + 1 - startY);
    doc.line(margin + colW, startY, margin + colW, y + rowH + 1);

    return y + rowH + 1;
  };

  const drawNetPayBox = (doc, y, pageW, margin, accent) => {
    const [r, g, b] = hexToRgbArr(accent);
    const tableW = pageW - margin * 2;
    doc.setFillColor(r, g, b);
    doc.rect(margin, y, tableW, 12, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('NET PAY', margin + 4, y + 7.8);
    doc.text(`Rs. ${formatINR(totals.netPayRounded)}`, margin + tableW - 4, y + 7.8, { align: 'right' });

    doc.setTextColor(40, 40, 40);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.text(`In Words: ${totals.netInWords}`, margin, y + 19);

    return y + 24;
  };

  // ---------- 5 templates ----------
  const renderClassic = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 14;
    const accent = TEMPLATES[0].accent;
    const [r, g, b] = hexToRgbArr(accent);

    // Header
    doc.setFillColor(r, g, b);
    doc.rect(0, 0, pageW, 26, 'F');
    drawPdfLogo(doc, logo, margin, 4, 18, company.name, [255, 255, 255]);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(company.name, margin + 22, 12);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(company.address, margin + 22, 18);
    doc.text(`${company.phone}  |  ${company.email}`, margin + 22, 23);

    // Title bar
    let y = 34;
    doc.setTextColor(30, 30, 30);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text(`Salary Slip for ${periodLabel}`, pageW / 2, y, { align: 'center' });

    // Employee info box
    y += 6;
    doc.setDrawColor(220, 220, 220);
    doc.setFillColor(248, 250, 252);
    doc.rect(margin, y, pageW - margin * 2, 32, 'FD');
    doc.setFontSize(9.5);
    const colX1 = margin + 3, colX2 = pageW / 2 + 4;
    const lh = 5.5;
    const writePair = (x, yy, k, v) => {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(80, 80, 80);
      doc.text(k, x, yy);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(20, 20, 20);
      doc.text(String(v), x + 34, yy);
    };
    let yy = y + 5.5;
    writePair(colX1, yy, 'Employee:', employee.name);
    writePair(colX2, yy, 'Emp ID:', employee.empId);
    yy += lh;
    writePair(colX1, yy, 'Designation:', employee.designation);
    writePair(colX2, yy, 'Department:', employee.department);
    yy += lh;
    writePair(colX1, yy, 'Date of Joining:', employee.doj);
    writePair(colX2, yy, 'PAN:', employee.pan);
    yy += lh;
    writePair(colX1, yy, 'UAN:', employee.uan);
    writePair(colX2, yy, 'Bank A/C:', `${employee.bankAcc} (${employee.bankName})`);
    yy += lh;
    writePair(colX1, yy, 'Paid Days:', `${period.paidDays} / ${period.workingDays}`);
    writePair(colX2, yy, 'LOP Days:', period.lop);

    y += 36;
    y = drawEarningsDeductionsTable(doc, y, pageW, margin, accent);
    y += 4;
    y = drawNetPayBox(doc, y, pageW, margin, accent);

    // Footer
    y += 8;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8.5);
    doc.setTextColor(120, 120, 120);
    doc.text('This is a system-generated salary slip and does not require a signature.', pageW / 2, y, { align: 'center' });

    return doc;
  };

  const renderModern = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 14;
    const accent = TEMPLATES[1].accent;
    const [r, g, b] = hexToRgbArr(accent);

    // Tall banner
    doc.setFillColor(r, g, b);
    doc.rect(0, 0, pageW, 40, 'F');
    drawPdfLogo(doc, logo, margin, 6, 24, company.name, [255, 255, 255]);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.text(company.name.toUpperCase(), pageW / 2, 16, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(company.address, pageW / 2, 23, { align: 'center' });
    doc.text(`${company.phone}  •  ${company.email}`, pageW / 2, 29, { align: 'center' });
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(`PAYSLIP — ${periodLabel.toUpperCase()}`, pageW / 2, 36, { align: 'center' });

    let y = 48;
    doc.setTextColor(20, 20, 20);
    doc.setDrawColor(r, g, b);
    doc.setLineWidth(0.6);
    doc.line(margin, y, pageW - margin, y);
    doc.setLineWidth(0.2);

    y += 6;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(employee.name, margin, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.text(`${employee.designation} • ${employee.department}`, margin, y + 5);
    doc.text(`Emp ID: ${employee.empId}   |   PAN: ${employee.pan}   |   UAN: ${employee.uan}`, margin, y + 10);
    doc.text(`Bank: ${employee.bankName} • A/C ${employee.bankAcc}   |   Paid: ${period.paidDays}/${period.workingDays} (LOP ${period.lop})`, margin, y + 15);

    y += 22;
    y = drawEarningsDeductionsTable(doc, y, pageW, margin, accent);
    y += 5;
    y = drawNetPayBox(doc, y, pageW, margin, accent);

    y += 10;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8.5);
    doc.setTextColor(120, 120, 120);
    doc.text('Computer-generated payslip. Confidential.', pageW / 2, y, { align: 'center' });

    return doc;
  };

  const renderMinimal = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 16;
    const accent = TEMPLATES[2].accent;

    doc.setTextColor(20, 20, 20);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    drawPdfLogo(doc, logo, margin, 8, 14, company.name, [240, 240, 240]);
    doc.text(company.name, margin + 18, 18);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(110, 110, 110);
    doc.text(`${company.address}  |  ${company.phone}  |  ${company.email}`, margin + 18, 24);

    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.4);
    doc.line(margin, 28, pageW - margin, 28);
    doc.setLineWidth(0.2);

    doc.setTextColor(20, 20, 20);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('PAYSLIP', margin, 36);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(periodLabel, pageW - margin, 36, { align: 'right' });

    let y = 44;
    doc.setFontSize(9.5);
    const lh = 5.2;
    const line = (k, v) => {
      doc.setTextColor(110, 110, 110);
      doc.text(k, margin, y);
      doc.setTextColor(20, 20, 20);
      doc.text(String(v), margin + 38, y);
      y += lh;
    };
    line('Name', employee.name);
    line('Employee ID', employee.empId);
    line('Designation', `${employee.designation} (${employee.department})`);
    line('PAN / UAN', `${employee.pan}  •  ${employee.uan}`);
    line('Bank', `${employee.bankName} — ${employee.bankAcc}`);
    line('Paid / Working', `${period.paidDays} / ${period.workingDays}  (LOP ${period.lop})`);

    y += 2;
    y = drawEarningsDeductionsTable(doc, y, pageW, margin, accent);
    y += 4;
    y = drawNetPayBox(doc, y, pageW, margin, accent);

    y += 8;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8.5);
    doc.setTextColor(140, 140, 140);
    doc.text('System-generated. No signature required.', margin, y);

    return doc;
  };

  const renderSidebar = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const accent = TEMPLATES[3].accent;
    const [r, g, b] = hexToRgbArr(accent);
    const sideW = 52;

    // Side strip
    doc.setFillColor(r, g, b);
    doc.rect(0, 0, sideW, pageH, 'F');
    drawPdfLogo(doc, logo, (sideW - 28) / 2, 6, 28, company.name, [255, 255, 255]);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    const wrapped = doc.splitTextToSize(company.name, sideW - 8);
    doc.text(wrapped, 4, 42);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    let sy = 42 + wrapped.length * 6 + 4;
    doc.text(doc.splitTextToSize(company.address, sideW - 8), 4, sy); sy += 14;
    doc.text(company.phone, 4, sy); sy += 5;
    doc.text(company.email, 4, sy); sy += 10;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('PAY PERIOD', 4, sy); sy += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(periodLabel, 4, sy); sy += 8;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('NET PAY', 4, sy); sy += 6;
    doc.setFontSize(14);
    doc.text(`Rs. ${formatINR(totals.netPayRounded)}`, 4, sy);

    // Right body
    const bodyX = sideW + 8;
    let y = 14;
    doc.setTextColor(20, 20, 20);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.text('Salary Slip', bodyX, y);
    y += 8;

    doc.setFontSize(9.5);
    const lh = 5.4;
    const line = (k, v) => {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(80, 80, 80);
      doc.text(k, bodyX, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(20, 20, 20);
      doc.text(String(v), bodyX + 34, y);
      y += lh;
    };
    line('Employee:', employee.name);
    line('Emp ID:', employee.empId);
    line('Designation:', employee.designation);
    line('Department:', employee.department);
    line('PAN:', employee.pan);
    line('UAN:', employee.uan);
    line('Bank:', `${employee.bankName} — ${employee.bankAcc}`);
    line('Paid / Working:', `${period.paidDays} / ${period.workingDays} (LOP ${period.lop})`);

    y += 3;
    // Use full width to right margin
    const margin = sideW + 4;
    y = drawEarningsDeductionsTable(doc, y, pageW + margin - 14, margin, accent);
    y += 4;
    y = drawNetPayBox(doc, y, pageW + margin - 14, margin, accent);

    return doc;
  };

  const renderCompact = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 14;
    const accent = TEMPLATES[4].accent;
    const [r, g, b] = hexToRgbArr(accent);

    // Slim header
    doc.setDrawColor(r, g, b);
    doc.setLineWidth(1.2);
    doc.line(margin, 12, pageW - margin, 12);
    doc.setLineWidth(0.2);

    drawPdfLogo(doc, logo, margin, 14, 14, company.name, [240, 240, 240]);
    doc.setTextColor(20, 20, 20);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text(company.name, margin + 18, 20);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text(`${company.address}  •  ${company.phone}`, margin + 18, 25);

    doc.setTextColor(r, g, b);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(`PAYSLIP — ${periodLabel}`, pageW - margin, 20, { align: 'right' });

    let y = 32;
    // Two-column employee info
    const colW = (pageW - margin * 2) / 2;
    doc.setFillColor(248, 248, 252);
    doc.rect(margin, y, pageW - margin * 2, 26, 'F');
    doc.setDrawColor(230, 230, 230);
    doc.rect(margin, y, pageW - margin * 2, 26);

    doc.setTextColor(20, 20, 20);
    doc.setFontSize(9);
    const lh = 4.6;
    const writeCol = (col, items) => {
      let yy = y + 5;
      items.forEach(([k, v]) => {
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(90, 90, 90);
        doc.text(k, margin + 3 + col * colW, yy);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(20, 20, 20);
        doc.text(String(v), margin + 32 + col * colW, yy);
        yy += lh;
      });
    };
    writeCol(0, [
      ['Name:', employee.name],
      ['Emp ID:', employee.empId],
      ['Designation:', employee.designation],
      ['Department:', employee.department],
      ['Date of Joining:', employee.doj],
    ]);
    writeCol(1, [
      ['PAN:', employee.pan],
      ['UAN:', employee.uan],
      ['Bank:', employee.bankName],
      ['A/C:', employee.bankAcc],
      ['Paid Days:', `${period.paidDays}/${period.workingDays} (LOP ${period.lop})`],
    ]);

    y += 30;
    y = drawEarningsDeductionsTable(doc, y, pageW, margin, accent);
    y += 4;
    y = drawNetPayBox(doc, y, pageW, margin, accent);

    y += 10;
    // Signature lines
    const sigW = (pageW - margin * 2 - 10) / 2;
    doc.setDrawColor(180, 180, 180);
    doc.line(margin, y + 14, margin + sigW, y + 14);
    doc.line(pageW - margin - sigW, y + 14, pageW - margin, y + 14);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(80, 80, 80);
    doc.text('Employee Signature', margin, y + 19);
    doc.text('Authorised Signatory', pageW - margin, y + 19, { align: 'right' });

    return doc;
  };

  const TEMPLATE_RENDERERS = {
    classic: renderClassic,
    modern: renderModern,
    minimal: renderMinimal,
    sidebar: renderSidebar,
    compact: renderCompact,
  };

  const buildPDF = () => TEMPLATE_RENDERERS[template]();

  const handleDownloadPDF = () => {
    const doc = buildPDF();
    doc.save(`Salary_Slip_${employee.empId || 'Employee'}_${period.month}_${period.year}.pdf`);
  };

  const handlePrint = () => {
    const doc = buildPDF();
    doc.autoPrint();
    window.open(doc.output('bloburl'), '_blank');
  };

  const handleReset = () => {
    setTemplate('classic');
    setCompany({
      name: 'Acme Technologies Pvt. Ltd.',
      address: 'Plot 12, Cyber City, Gurugram 122002',
      phone: '+91 98765 43210',
      email: 'hr@acmetech.in',
    });
    setEmployee({
      name: 'Rahul Sharma',
      empId: 'ACM-1042',
      designation: 'Senior Software Engineer',
      department: 'Engineering',
      doj: '15/03/2021',
      pan: 'ABCDE1234F',
      uan: '101234567890',
      bankAcc: 'XXXX XXXX 4521',
      bankName: 'HDFC Bank',
      paymentMode: 'Bank Transfer',
    });
    setPeriod({
      month: currentMonth,
      year: String(currentYear),
      workingDays: 30,
      paidDays: 30,
      lop: 0,
    });
    setEarnings([
      newRow('Basic Salary', 40000),
      newRow('House Rent Allowance', 16000),
      newRow('Conveyance Allowance', 1600),
      newRow('Medical Allowance', 1250),
      newRow('Special Allowance', 11150),
    ]);
    setDeductions([
      newRow('Provident Fund (PF)', 1800),
      newRow('Professional Tax', 200),
      newRow('Income Tax (TDS)', 3500),
    ]);
  };

  const activeTpl = TEMPLATES.find((t) => t.id === template) || TEMPLATES[0];

  return (
    <div className="calculator-page fade-in">
      <SEO
        title="Salary Slip Generator"
        description="Create professional salary slips / payslips with 5 printable templates, full earnings & deductions breakdown, and downloadable PDF."
        keywords="salary slip generator, payslip generator, salary slip format, payslip template, monthly salary slip pdf"
        canonicalPath="/salary-slip"
        calculatorType="SalarySlipGenerator"
        calculatorDescription="Generate itemised salary slips with earnings, deductions, net pay in words and 5 printable templates."
      />

      <div className="calculator-header">
        <h1>Salary Slip Generator</h1>
        <p>Itemised payslips with earnings, deductions and 5 printable templates</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          {/* Template picker */}
          <div className="input-group">
            <label>Template</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {TEMPLATES.map((t) => {
                const active = t.id === template;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTemplate(t.id)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 999,
                      border: `1.5px solid ${active ? t.accent : '#d1d5db'}`,
                      background: active ? t.accent : '#fff',
                      color: active ? '#fff' : '#374151',
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <LogoUploader logo={logo} setLogo={setLogo} label="Company Logo" accent={activeTpl.accent} />

          {/* Company */}
          <h3 style={{ marginTop: 16 }}>Company Details</h3>
          <div className="input-group">
            <label>Company Name</label>
            <input type="text" value={company.name}
              onChange={(e) => setCompany({ ...company, name: e.target.value })} />
          </div>
          <div className="input-group">
            <label>Address</label>
            <input type="text" value={company.address}
              onChange={(e) => setCompany({ ...company, address: e.target.value })} />
          </div>
          <div className="flex-row">
            <div className="input-group">
              <label>Phone</label>
              <input type="text" value={company.phone}
                onChange={(e) => setCompany({ ...company, phone: e.target.value })} />
            </div>
            <div className="input-group">
              <label>Email</label>
              <input type="text" value={company.email}
                onChange={(e) => setCompany({ ...company, email: e.target.value })} />
            </div>
          </div>

          {/* Employee */}
          <h3 style={{ marginTop: 16 }}>Employee Details</h3>
          <div className="flex-row">
            <div className="input-group">
              <label>Employee Name</label>
              <input type="text" value={employee.name}
                onChange={(e) => setEmployee({ ...employee, name: e.target.value })} />
            </div>
            <div className="input-group">
              <label>Employee ID</label>
              <input type="text" value={employee.empId}
                onChange={(e) => setEmployee({ ...employee, empId: e.target.value })} />
            </div>
          </div>
          <div className="flex-row">
            <div className="input-group">
              <label>Designation</label>
              <input type="text" value={employee.designation}
                onChange={(e) => setEmployee({ ...employee, designation: e.target.value })} />
            </div>
            <div className="input-group">
              <label>Department</label>
              <input type="text" value={employee.department}
                onChange={(e) => setEmployee({ ...employee, department: e.target.value })} />
            </div>
          </div>
          <div className="flex-row">
            <div className="input-group">
              <label>Date of Joining</label>
              <input type="text" value={employee.doj}
                onChange={(e) => setEmployee({ ...employee, doj: e.target.value })} />
            </div>
            <div className="input-group">
              <label>PAN</label>
              <input type="text" value={employee.pan}
                onChange={(e) => setEmployee({ ...employee, pan: e.target.value })} />
            </div>
            <div className="input-group">
              <label>UAN</label>
              <input type="text" value={employee.uan}
                onChange={(e) => setEmployee({ ...employee, uan: e.target.value })} />
            </div>
          </div>
          <div className="flex-row">
            <div className="input-group">
              <label>Bank Name</label>
              <input type="text" value={employee.bankName}
                onChange={(e) => setEmployee({ ...employee, bankName: e.target.value })} />
            </div>
            <div className="input-group">
              <label>Bank A/C</label>
              <input type="text" value={employee.bankAcc}
                onChange={(e) => setEmployee({ ...employee, bankAcc: e.target.value })} />
            </div>
            <div className="input-group">
              <label>Payment Mode</label>
              <select value={employee.paymentMode}
                onChange={(e) => setEmployee({ ...employee, paymentMode: e.target.value })}>
                {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>

          {/* Period */}
          <h3 style={{ marginTop: 16 }}>Pay Period</h3>
          <div className="flex-row">
            <div className="input-group">
              <label>Month</label>
              <select value={period.month}
                onChange={(e) => setPeriod({ ...period, month: e.target.value })}>
                {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="input-group">
              <label>Year</label>
              <input type="text" value={period.year}
                onChange={(e) => setPeriod({ ...period, year: e.target.value })} />
            </div>
            <div className="input-group">
              <label>Working Days</label>
              <input type="number" value={period.workingDays}
                onChange={(e) => setPeriod({ ...period, workingDays: Number(e.target.value) })} />
            </div>
            <div className="input-group">
              <label>Paid Days</label>
              <input type="number" value={period.paidDays}
                onChange={(e) => setPeriod({ ...period, paidDays: Number(e.target.value) })} />
            </div>
            <div className="input-group">
              <label>LOP Days</label>
              <input type="number" value={period.lop}
                onChange={(e) => setPeriod({ ...period, lop: Number(e.target.value) })} />
            </div>
          </div>

          {/* Earnings */}
          <h3 style={{ marginTop: 16 }}>Earnings</h3>
          {earnings.map((row) => (
            <div key={row.id} style={{
              display: 'grid', gridTemplateColumns: '2fr 120px 40px',
              gap: 8, alignItems: 'center', marginBottom: 8,
            }}>
              <input type="text" value={row.label}
                onChange={(e) => updateRow(setEarnings)(row.id, { label: e.target.value })} />
              <input type="number" value={row.amount}
                onChange={(e) => updateRow(setEarnings)(row.id, { amount: Number(e.target.value) })} />
              <button type="button" className="btn btn-outline"
                onClick={() => removeRow(setEarnings)(row.id)}
                style={{ padding: '6px 8px' }} title="Remove">
                <FaTrash />
              </button>
            </div>
          ))}
          <button type="button" className="btn btn-outline"
            onClick={addRow(setEarnings, 'New Earning')}>
            <FaPlus /> Add Earning
          </button>

          {/* Deductions */}
          <h3 style={{ marginTop: 16 }}>Deductions</h3>
          {deductions.map((row) => (
            <div key={row.id} style={{
              display: 'grid', gridTemplateColumns: '2fr 120px 40px',
              gap: 8, alignItems: 'center', marginBottom: 8,
            }}>
              <input type="text" value={row.label}
                onChange={(e) => updateRow(setDeductions)(row.id, { label: e.target.value })} />
              <input type="number" value={row.amount}
                onChange={(e) => updateRow(setDeductions)(row.id, { amount: Number(e.target.value) })} />
              <button type="button" className="btn btn-outline"
                onClick={() => removeRow(setDeductions)(row.id)}
                style={{ padding: '6px 8px' }} title="Remove">
                <FaTrash />
              </button>
            </div>
          ))}
          <button type="button" className="btn btn-outline"
            onClick={addRow(setDeductions, 'New Deduction')}>
            <FaPlus /> Add Deduction
          </button>

          {/* Summary */}
          <div style={{
            marginTop: 18, padding: 14, borderRadius: 10,
            background: '#ecfdf5', border: '1px solid #a7f3d0',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
              <span>Gross Earnings</span>
              <strong>Rs. {formatINR(totals.grossEarnings)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginTop: 4 }}>
              <span>Total Deductions</span>
              <strong>Rs. {formatINR(totals.totalDeductions)}</strong>
            </div>
            <hr style={{ margin: '8px 0', border: 0, borderTop: '1px dashed #6ee7b7' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16 }}>
              <strong>Net Pay</strong>
              <strong style={{ color: '#047857' }}>Rs. {formatINR(totals.netPayRounded)}</strong>
            </div>
            <div style={{ fontSize: 12, color: '#065f46', marginTop: 4, fontStyle: 'italic' }}>
              {totals.netInWords}
            </div>
          </div>

          <div className="button-group" style={{ marginTop: 16 }}>
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo /> Reset
            </button>
            <button className="btn btn-primary" onClick={handleDownloadPDF}>
              <FaDownload /> Download PDF
            </button>
            <button className="btn btn-primary" onClick={handlePrint}>
              <FaPrint /> Print
            </button>
          </div>
        </div>

        {/* Live preview */}
        <div className="calculator-results">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FaIdBadge style={{ color: activeTpl.accent }} /> Live Preview — {activeTpl.label}
          </h3>
          <SlipPreview
            template={template}
            company={company}
            employee={employee}
            period={period}
            periodLabel={periodLabel}
            earnings={earnings}
            deductions={deductions}
            totals={totals}
            accent={activeTpl.accent}
            logo={logo}
          />
        </div>
      </div>
    </div>
  );
};

// ---------- Preview helpers ----------
const Row = ({ k, v }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, padding: '2px 0' }}>
    <span style={{ color: '#6b7280' }}>{k}</span>
    <span style={{ color: '#111827', fontWeight: 500 }}>{v}</span>
  </div>
);

const EarnDedTable = ({ earnings, deductions, totals, accent }) => {
  const max = Math.max(earnings.length, deductions.length);
  const rows = [];
  for (let i = 0; i < max; i++) rows.push({ e: earnings[i], d: deductions[i] });
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, marginTop: 8 }}>
      <thead>
        <tr style={{ background: accent, color: '#fff' }}>
          <th style={{ textAlign: 'left', padding: '6px 8px' }}>Earnings</th>
          <th style={{ textAlign: 'right', padding: '6px 8px' }}>Amount</th>
          <th style={{ textAlign: 'left', padding: '6px 8px', borderLeft: '1px solid rgba(255,255,255,0.3)' }}>Deductions</th>
          <th style={{ textAlign: 'right', padding: '6px 8px' }}>Amount</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} style={{ background: i % 2 ? '#f8fafc' : '#fff' }}>
            <td style={{ padding: '5px 8px' }}>{r.e?.label || ''}</td>
            <td style={{ padding: '5px 8px', textAlign: 'right' }}>{r.e ? formatINR(r.e.amount) : ''}</td>
            <td style={{ padding: '5px 8px', borderLeft: '1px solid #e5e7eb' }}>{r.d?.label || ''}</td>
            <td style={{ padding: '5px 8px', textAlign: 'right' }}>{r.d ? formatINR(r.d.amount) : ''}</td>
          </tr>
        ))}
        <tr style={{ background: '#f0f4f8', fontWeight: 700 }}>
          <td style={{ padding: '6px 8px' }}>Gross Earnings</td>
          <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatINR(totals.grossEarnings)}</td>
          <td style={{ padding: '6px 8px', borderLeft: '1px solid #e5e7eb' }}>Total Deductions</td>
          <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatINR(totals.totalDeductions)}</td>
        </tr>
      </tbody>
    </table>
  );
};

const NetPayBlock = ({ totals, accent }) => (
  <div style={{
    marginTop: 8, background: accent, color: '#fff',
    padding: '10px 12px', display: 'flex', justifyContent: 'space-between',
    fontWeight: 700, fontSize: 14,
  }}>
    <span>NET PAY</span>
    <span>Rs. {formatINR(totals.netPayRounded)}</span>
  </div>
);

const LogoImg = ({ logo, size = 40, bg = '#fff' }) => (
  logo ? (
    <div style={{ width: size, height: size, background: bg, borderRadius: 6, padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <img src={logo} alt="logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
    </div>
  ) : null
);

const SlipPreview = ({ template, company, employee, period, periodLabel, earnings, deductions, totals, accent, logo }) => {
  const empBlock = (
    <>
      <Row k="Employee" v={employee.name} />
      <Row k="Emp ID" v={employee.empId} />
      <Row k="Designation" v={`${employee.designation} • ${employee.department}`} />
      <Row k="PAN / UAN" v={`${employee.pan} • ${employee.uan}`} />
      <Row k="Bank" v={`${employee.bankName} — ${employee.bankAcc}`} />
      <Row k="Paid / Working" v={`${period.paidDays} / ${period.workingDays} (LOP ${period.lop})`} />
    </>
  );

  const containerBase = {
    background: '#fff', borderRadius: 8, padding: 16,
    border: '1px solid #e5e7eb', marginTop: 10, color: '#111827',
  };

  if (template === 'classic') {
    return (
      <div style={containerBase}>
        <div style={{ background: accent, color: '#fff', padding: '10px 12px', margin: '-16px -16px 12px', display: 'flex', gap: 12, alignItems: 'center' }}>
          <LogoImg logo={logo} size={46} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{company.name}</div>
            <div style={{ fontSize: 11.5, opacity: 0.9 }}>{company.address}</div>
            <div style={{ fontSize: 11.5, opacity: 0.9 }}>{company.phone} | {company.email}</div>
          </div>
        </div>
        <div style={{ textAlign: 'center', fontWeight: 700, marginBottom: 8 }}>
          Salary Slip for {periodLabel}
        </div>
        <div style={{ background: '#f8fafc', padding: 10, borderRadius: 6 }}>{empBlock}</div>
        <EarnDedTable earnings={earnings} deductions={deductions} totals={totals} accent={accent} />
        <NetPayBlock totals={totals} accent={accent} />
        <div style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic', marginTop: 8, textAlign: 'center' }}>
          In Words: {totals.netInWords}
        </div>
      </div>
    );
  }

  if (template === 'modern') {
    return (
      <div style={containerBase}>
        <div style={{ background: accent, color: '#fff', padding: '14px 12px', margin: '-16px -16px 12px', textAlign: 'center', position: 'relative' }}>
          {logo && <div style={{ position: 'absolute', left: 12, top: 12 }}><LogoImg logo={logo} size={46} /></div>}
          <div style={{ fontWeight: 800, fontSize: 16, letterSpacing: 0.5 }}>{company.name.toUpperCase()}</div>
          <div style={{ fontSize: 11.5, opacity: 0.92 }}>{company.address}</div>
          <div style={{ fontSize: 11.5, opacity: 0.92 }}>{company.phone} • {company.email}</div>
          <div style={{ fontWeight: 700, fontSize: 12.5, marginTop: 4 }}>PAYSLIP — {periodLabel.toUpperCase()}</div>
        </div>
        <div style={{ fontWeight: 700 }}>{employee.name}</div>
        <div style={{ fontSize: 12, color: '#6b7280' }}>{employee.designation} • {employee.department}</div>
        <div style={{ fontSize: 12, color: '#6b7280' }}>Emp {employee.empId} | PAN {employee.pan} | UAN {employee.uan}</div>
        <div style={{ fontSize: 12, color: '#6b7280' }}>{employee.bankName} • {employee.bankAcc} | Paid {period.paidDays}/{period.workingDays}</div>
        <EarnDedTable earnings={earnings} deductions={deductions} totals={totals} accent={accent} />
        <NetPayBlock totals={totals} accent={accent} />
      </div>
    );
  }

  if (template === 'minimal') {
    return (
      <div style={containerBase}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <LogoImg logo={logo} size={44} bg="#f3f4f6" />
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{company.name}</div>
            <div style={{ fontSize: 11.5, color: '#6b7280' }}>
              {company.address} | {company.phone} | {company.email}
            </div>
          </div>
        </div>
        <hr style={{ margin: '8px 0', borderTop: '1px solid #111827' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
          <span>PAYSLIP</span><span>{periodLabel}</span>
        </div>
        <div style={{ marginTop: 8 }}>{empBlock}</div>
        <EarnDedTable earnings={earnings} deductions={deductions} totals={totals} accent={accent} />
        <NetPayBlock totals={totals} accent={accent} />
      </div>
    );
  }

  if (template === 'sidebar') {
    return (
      <div style={{ ...containerBase, padding: 0, overflow: 'hidden', display: 'flex' }}>
        <div style={{ background: accent, color: '#fff', padding: 14, width: 150, minWidth: 150 }}>
          {logo && <div style={{ marginBottom: 10 }}><LogoImg logo={logo} size={56} /></div>}
          <div style={{ fontWeight: 700, fontSize: 13 }}>{company.name}</div>
          <div style={{ fontSize: 11, marginTop: 6, opacity: 0.9 }}>{company.address}</div>
          <div style={{ fontSize: 11, marginTop: 4, opacity: 0.9 }}>{company.phone}</div>
          <div style={{ fontSize: 11, opacity: 0.9 }}>{company.email}</div>
          <div style={{ marginTop: 12, fontWeight: 700, fontSize: 11 }}>PAY PERIOD</div>
          <div style={{ fontSize: 12 }}>{periodLabel}</div>
          <div style={{ marginTop: 12, fontWeight: 700, fontSize: 11 }}>NET PAY</div>
          <div style={{ fontWeight: 800, fontSize: 14 }}>Rs. {formatINR(totals.netPayRounded)}</div>
        </div>
        <div style={{ padding: 14, flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>Salary Slip</div>
          {empBlock}
          <EarnDedTable earnings={earnings} deductions={deductions} totals={totals} accent={accent} />
        </div>
      </div>
    );
  }

  // compact
  return (
    <div style={containerBase}>
      <div style={{ borderTop: `3px solid ${accent}`, paddingTop: 8, display: 'flex', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <LogoImg logo={logo} size={44} bg="#f3f4f6" />
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{company.name}</div>
            <div style={{ fontSize: 11.5, color: '#6b7280' }}>{company.address} • {company.phone}</div>
          </div>
        </div>
        <div style={{ color: accent, fontWeight: 700 }}>PAYSLIP — {periodLabel}</div>
      </div>
      <div style={{ background: '#f8fafc', padding: 10, borderRadius: 6, marginTop: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        <div>
          <Row k="Name" v={employee.name} />
          <Row k="Emp ID" v={employee.empId} />
          <Row k="Designation" v={employee.designation} />
          <Row k="Department" v={employee.department} />
          <Row k="DOJ" v={employee.doj} />
        </div>
        <div>
          <Row k="PAN" v={employee.pan} />
          <Row k="UAN" v={employee.uan} />
          <Row k="Bank" v={employee.bankName} />
          <Row k="A/C" v={employee.bankAcc} />
          <Row k="Paid Days" v={`${period.paidDays}/${period.workingDays} (LOP ${period.lop})`} />
        </div>
      </div>
      <EarnDedTable earnings={earnings} deductions={deductions} totals={totals} accent={accent} />
      <NetPayBlock totals={totals} accent={accent} />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 18, fontSize: 11.5, color: '#6b7280' }}>
        <span>______________________<br />Employee Signature</span>
        <span style={{ textAlign: 'right' }}>______________________<br />Authorised Signatory</span>
      </div>
    </div>
  );
};

export default SalarySlipGenerator;
